/**
 * Страница тестирования пациента.
 *
 * Содержит:
 * - список доступных тестов
 * - интерактивное прохождение тестов (время реакции, последовательности, точность)
 * - историю результатов
 *
 * Каждый тест реализован как мини-интерактивный модуль,
 * встроенный непосредственно в страницу.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { testsAPI } from '../../api/client';
import { TestInfo, TestResult } from '../../types';

const TestsPage: React.FC = () => {
  const [tests, setTests] = useState<TestInfo[]>([]);
  const [results, setResults] = useState<TestResult[]>([]);
  const [activeTest, setActiveTest] = useState<TestInfo | null>(null);
  const [testPhase, setTestPhase] = useState<'idle' | 'running' | 'completed'>('idle');
  const [totalResults, setTotalResults] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    const loadData = async () => {
      try {
        const [testsRes, resultsRes] = await Promise.all([
          testsAPI.getTests(),
          testsAPI.getResults({ limit: 20 }),
        ]);
        setTests(testsRes.data.tests);
        setResults(resultsRes.data.results);
        setTotalResults(resultsRes.data.total ?? resultsRes.data.results.length);
      } catch (err) {
        console.error('Ошибка загрузки тестов:', err);
      }
    };
    loadData();
  }, []);

  /** Отправка результата теста на сервер */
  const submitResult = useCallback(async (testId: number, score: number, details: Record<string, unknown>) => {
    try {
      await testsAPI.submitResult({ test_id: testId, score, details });
      const res = await testsAPI.getResults({ limit: PAGE_SIZE });
      setResults(res.data.results);
      setTotalResults(res.data.total ?? res.data.results.length);
    } catch (err) {
      console.error('Ошибка сохранения результата:', err);
    }
  }, []);

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>Тестирование</h1>

      {/* Если тест не запущен — показываем список */}
      {!activeTest && (
        <>
          <div style={styles.testsGrid}>
            {tests.map((test) => (
              <div key={test.id} style={styles.testCard}>
                <div style={styles.testType}>
                  {test.type === 'cognitive' ? 'Когнитивный' : 'Моторный'}
                </div>
                <h3 style={styles.testName}>{test.name}</h3>
                <p style={styles.testDesc}>{test.description}</p>
                <button
                  style={styles.startBtn}
                  onClick={() => { setActiveTest(test); setTestPhase('idle'); }}
                >
                  Начать тест
                </button>
              </div>
            ))}
          </div>

          {/* История результатов */}
          <h2 style={{ marginTop: 32, marginBottom: 16 }}>Мои результаты</h2>
          {results.length === 0 ? (
            <p style={{ color: '#6b7280' }}>Вы ещё не проходили тесты</p>
          ) : (
            <div>
              {results.map((r) => (
                <div key={r.id} style={styles.resultRow}>
                  <span style={{ fontWeight: 500 }}>{r.test_name}</span>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>
                    {new Date(r.completed_at).toLocaleDateString('ru-RU')}
                  </span>
                  <span style={{
                    fontWeight: 700,
                    color: r.normalized_score >= 7 ? '#dc2626' :
                           r.normalized_score >= 4 ? '#f59e0b' : '#16a34a',
                  }}>
                    {r.normalized_score.toFixed(1)}/10
                  </span>
                </div>
              ))}
              {results.length < totalResults && (
                <button onClick={async () => {
                  try {
                    const res = await testsAPI.getResults({ limit: PAGE_SIZE, offset: results.length });
                    setResults([...results, ...res.data.results]);
                    setTotalResults(res.data.total ?? totalResults);
                  } catch (err) {
                    console.error('Ошибка загрузки:', err);
                  }
                }} style={styles.loadMoreBtn}>
                  Загрузить ещё ({totalResults - results.length})
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Экран прохождения теста */}
      {activeTest && (
        <div style={styles.testScreen}>
          <button
            style={styles.backBtn}
            onClick={() => { setActiveTest(null); setTestPhase('idle'); }}
          >
            &larr; Назад к списку
          </button>

          <h2>{activeTest.name}</h2>
          <p style={{ color: '#6b7280', marginBottom: 20 }}>{activeTest.instructions}</p>

          {/* Тест на время реакции */}
          {activeTest.test_key === 'reaction_time' && (
            <ReactionTimeTest
              test={activeTest}
              onComplete={(score, details) => {
                submitResult(activeTest.id, score, details);
                setTestPhase('completed');
              }}
            />
          )}

          {/* Тест на последовательности */}
          {activeTest.test_key === 'sequence' && (
            <SequenceTest
              test={activeTest}
              onComplete={(score, details) => {
                submitResult(activeTest.id, score, details);
                setTestPhase('completed');
              }}
            />
          )}

          {/* Тест на точность нажатий */}
          {activeTest.test_key === 'accuracy' && (
            <AccuracyTest
              test={activeTest}
              onComplete={(score, details) => {
                submitResult(activeTest.id, score, details);
                setTestPhase('completed');
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

/* ============================================================
 * Компонент: Тест на время реакции
 * Пациент нажимает кнопку при появлении зелёного сигнала.
 * Измеряется среднее время реакции (мс).
 * ============================================================ */
interface TestComponentProps {
  test: TestInfo;
  onComplete: (score: number, details: Record<string, unknown>) => void;
}

const ReactionTimeTest: React.FC<TestComponentProps> = ({ test, onComplete }) => {
  const [phase, setPhase] = useState<'ready' | 'waiting' | 'click' | 'done'>('ready');
  const [attempts, setAttempts] = useState<number[]>([]);
  const [startTime, setStartTime] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const TOTAL_ATTEMPTS = 5;

  const startRound = () => {
    setPhase('waiting');
    // Случайная задержка 1-4 секунды перед появлением сигнала
    const delay = 1000 + Math.random() * 3000;
    timerRef.current = setTimeout(() => {
      setPhase('click');
      setStartTime(Date.now());
    }, delay);
  };

  const handleClick = () => {
    if (phase === 'click') {
      const reaction = Date.now() - startTime;
      const newAttempts = [...attempts, reaction];
      setAttempts(newAttempts);

      if (newAttempts.length >= TOTAL_ATTEMPTS) {
        setPhase('done');
        const avg = Math.round(newAttempts.reduce((a, b) => a + b, 0) / newAttempts.length);
        onComplete(avg, {
          average_reaction_ms: avg,
          attempts: newAttempts,
          total_attempts: TOTAL_ATTEMPTS,
        });
      } else {
        setPhase('ready');
      }
    }
  };

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return (
    <div style={{ textAlign: 'center' }}>
      <p>Попытка {Math.min(attempts.length + 1, TOTAL_ATTEMPTS)} из {TOTAL_ATTEMPTS}</p>

      {phase === 'ready' && (
        <button onClick={startRound} style={styles.testBtn}>
          Начать попытку
        </button>
      )}

      {phase === 'waiting' && (
        <div style={{ ...styles.signalBox, backgroundColor: '#ef4444' }}>
          Ждите зелёный сигнал...
        </div>
      )}

      {phase === 'click' && (
        <div
          style={{ ...styles.signalBox, backgroundColor: '#16a34a', cursor: 'pointer' }}
          onClick={handleClick}
        >
          НАЖМИТЕ!
        </div>
      )}

      {phase === 'done' && (
        <div style={styles.resultBox}>
          <h3>Тест завершён!</h3>
          <p>Среднее время реакции: <strong>
            {Math.round(attempts.reduce((a, b) => a + b, 0) / attempts.length)} мс
          </strong></p>
          <p style={{ fontSize: 13, color: '#6b7280' }}>
            Результаты: {attempts.map((a) => `${a}мс`).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
};

/* ============================================================
 * Компонент: Тест на последовательности
 * Пациент запоминает и воспроизводит числовую последовательность.
 * ============================================================ */
const SequenceTest: React.FC<TestComponentProps> = ({ test, onComplete }) => {
  const [phase, setPhase] = useState<'show' | 'input' | 'done'>('show');
  const [sequence, setSequence] = useState<number[]>([]);
  const [userInput, setUserInput] = useState('');
  const [level, setLevel] = useState(3); // начинаем с 3 цифр
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const TOTAL_ROUNDS = 5;

  // Генерация новой последовательности
  const generateSequence = useCallback((length: number) => {
    const seq = Array.from({ length }, () => Math.floor(Math.random() * 10));
    setSequence(seq);
    setPhase('show');
    // Через 2 секунды прячем последовательность
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setPhase('input'), 2000);
  }, []);

  useEffect(() => {
    generateSequence(level);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const checkAnswer = () => {
    const userSeq = userInput.split('').map(Number);
    const correct = JSON.stringify(userSeq) === JSON.stringify(sequence);
    const newScore = score + (correct ? 20 : 0);
    const newRound = round + 1;

    setScore(newScore);
    setRound(newRound);
    setUserInput('');

    if (newRound >= TOTAL_ROUNDS) {
      setPhase('done');
      onComplete(newScore, {
        score: newScore,
        total_rounds: TOTAL_ROUNDS,
        max_level: level,
      });
    } else {
      const newLevel = correct ? level + 1 : Math.max(3, level - 1);
      setLevel(newLevel);
      generateSequence(newLevel);
    }
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <p>Раунд {Math.min(round + 1, TOTAL_ROUNDS)} из {TOTAL_ROUNDS} | Уровень: {level} цифр</p>

      {phase === 'show' && (
        <div style={styles.sequenceDisplay}>
          {sequence.map((n, i) => (
            <span key={i} style={styles.digit}>{n}</span>
          ))}
        </div>
      )}

      {phase === 'input' && (
        <div>
          <p style={{ marginBottom: 12 }}>Введите последовательность:</p>
          <input
            type="text" value={userInput}
            onChange={(e) => setUserInput(e.target.value.replace(/\D/g, ''))}
            style={styles.seqInput}
            maxLength={level}
            autoFocus
          />
          <br />
          <button onClick={checkAnswer} style={styles.testBtn}>
            Проверить
          </button>
        </div>
      )}

      {phase === 'done' && (
        <div style={styles.resultBox}>
          <h3>Тест завершён!</h3>
          <p>Результат: <strong>{score} из 100</strong></p>
        </div>
      )}
    </div>
  );
};

/* ============================================================
 * Компонент: Тест на точность нажатий
 * Пациент нажимает на появляющиеся цели (круги) в области.
 * ============================================================ */
const AccuracyTest: React.FC<TestComponentProps> = ({ test, onComplete }) => {
  const [phase, setPhase] = useState<'ready' | 'running' | 'done'>('ready');
  const [target, setTarget] = useState<{ x: number; y: number } | null>(null);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [totalTargets, setTotalTargets] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_TARGETS = 10;

  const spawnTarget = useCallback(() => {
    // Случайная позиция в области 300x300
    setTarget({
      x: 20 + Math.random() * 260,
      y: 20 + Math.random() * 260,
    });
  }, []);

  const startTest = () => {
    setPhase('running');
    setHits(0);
    setMisses(0);
    setTotalTargets(0);
    spawnTarget();
  };

  const handleTargetClick = () => {
    const newHits = hits + 1;
    const newTotal = totalTargets + 1;
    setHits(newHits);
    setTotalTargets(newTotal);

    if (newTotal >= MAX_TARGETS) {
      finishTest(newHits, misses, newTotal);
    } else {
      spawnTarget();
    }
  };

  const handleAreaClick = () => {
    // Клик мимо цели — промах
    setMisses((prev) => prev + 1);
  };

  const finishTest = (finalHits: number, finalMisses: number, total: number) => {
    setPhase('done');
    setTarget(null);
    // Очки: процент точных попаданий (чем больше, тем лучше)
    const accuracy = total > 0 ? (finalHits / (finalHits + finalMisses)) * 100 : 0;
    const finalScore = Math.round(accuracy);

    onComplete(finalScore, {
      hits: finalHits,
      misses: finalMisses,
      total_targets: total,
      accuracy_percent: Math.round(accuracy),
    });
  };

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return (
    <div style={{ textAlign: 'center' }}>
      {phase === 'ready' && (
        <button onClick={startTest} style={styles.testBtn}>
          Начать тест
        </button>
      )}

      {phase === 'running' && (
        <>
          <p>Цель {Math.min(totalTargets + 1, MAX_TARGETS)} из {MAX_TARGETS} | Попадания: {hits}</p>
          <div
            style={styles.targetArea}
            onClick={handleAreaClick}
          >
            {target && (
              <div
                style={{
                  ...styles.targetCircle,
                  left: target.x,
                  top: target.y,
                }}
                onClick={(e) => { e.stopPropagation(); handleTargetClick(); }}
              />
            )}
          </div>
        </>
      )}

      {phase === 'done' && (
        <div style={styles.resultBox}>
          <h3>Тест завершён!</h3>
          <p>Попадания: {hits}/{totalTargets}</p>
          <p>Промахи: {misses}</p>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  testsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 16,
  },
  testCard: {
    backgroundColor: 'var(--bg-card)', padding: 20, borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
  },
  testType: {
    fontSize: 12, color: '#2563eb', fontWeight: 600,
    textTransform: 'uppercase', marginBottom: 8,
  },
  testName: { fontSize: 18, marginBottom: 8 },
  testDesc: { fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 },
  startBtn: {
    backgroundColor: '#2563eb', color: '#fff', border: 'none',
    padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
  },
  testScreen: {
    backgroundColor: 'var(--bg-card)', padding: 24, borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
  },
  backBtn: {
    background: 'none', border: 'none', color: '#2563eb',
    cursor: 'pointer', fontSize: 14, marginBottom: 16, padding: 0,
  },
  testBtn: {
    backgroundColor: '#2563eb', color: '#fff', border: 'none',
    padding: '12px 32px', borderRadius: 8, cursor: 'pointer',
    fontSize: 16, fontWeight: 600, marginTop: 16,
  },
  signalBox: {
    width: 200, height: 200, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '24px auto', color: '#fff', fontSize: 18, fontWeight: 700,
    userSelect: 'none',
  },
  resultBox: {
    padding: 24, backgroundColor: 'var(--bg-success)', borderRadius: 12,
    marginTop: 20,
  },
  resultRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', backgroundColor: 'var(--bg-card)', borderRadius: 8,
    boxShadow: 'var(--shadow-card)', marginBottom: 8,
  },
  sequenceDisplay: {
    display: 'flex', gap: 12, justifyContent: 'center',
    margin: '24px 0', fontSize: 32, fontWeight: 700,
  },
  digit: {
    width: 48, height: 48, display: 'flex', alignItems: 'center',
    justifyContent: 'center', backgroundColor: '#2563eb', color: '#fff',
    borderRadius: 8,
  },
  seqInput: {
    padding: '10px 16px', fontSize: 24, textAlign: 'center',
    border: '2px solid #2563eb', borderRadius: 8, width: 200,
    letterSpacing: 8,
  },
  targetArea: {
    width: 320, height: 320, backgroundColor: 'var(--bg-btn-secondary)',
    borderRadius: 12, margin: '16px auto', position: 'relative',
    cursor: 'crosshair',
  },
  targetCircle: {
    width: 40, height: 40, borderRadius: '50%',
    backgroundColor: '#dc2626', position: 'absolute',
    cursor: 'pointer', transition: 'all 0.1s',
  },
  loadMoreBtn: {
    width: '100%', padding: '12px', backgroundColor: 'var(--bg-btn-secondary)',
    color: 'var(--text-btn-secondary)', border: 'none', borderRadius: 8,
    cursor: 'pointer', fontSize: 14, fontWeight: 500, marginTop: 8,
  },
};

export default TestsPage;
